/**
 * The Cancel + Reschedule reservation modals (DNKADM-0061 U3) — re-pointed to the
 * owned, atomic v2 reservation surface (handoff §5.15). Shared, controlled
 * (`open`/`onClose`) dialogs opened from the calendar drawer and the per-member panel.
 *
 * **Cancel (`CancelReservationDialog`).** A REQUIRED audit `reason` + a directive
 * (`policy | full | none`). On open it reads `getCancellationPreview(uuid, directive)`
 * once per choice, in parallel (DNKADM-0445), and says in plain words how the booking was
 * paid, when it starts, and what the member gets back under each choice (the copy lives
 * in `./cancelRefundCopy`). **An admin may still force `full`** — the admin cancel does
 * NOT honor the member refund-lock (the selector stays fully enabled even when
 * `refundLocked` is true; a notice explains the override). On confirm →
 * `cancelReservation(uuid, { reason, directive })`. No client-side refund math — the
 * previews are the source of truth. **A pending request (`unconfirmed`) is declined, not
 * cancelled (DNKADM-0446):** the same export renders the decline dialog, which always
 * sends `full`, offers no Refund choice, and says what the member gets back from the
 * `full` preview.
 *
 * **Reschedule (`RescheduleDialog`).** A REQUIRED reason; **credit-neutral**; the new
 * slot is chosen via the shared {@link AvailabilitySlotPicker} (the U2 availability
 * picker), with a manual ISO start fallback when availability is unavailable. On confirm
 * → `updateReservation(uuid, { start, reason })` (the `reservationUuid` survives the
 * reschedule). A new time that would hand the booking to a different coach comes back
 * `409 STAFF_WILL_CHANGE` and raises the `StaffChangeDialog` (DNKADM-0393 U5). Since
 * DNKADM-0488 the picker offers only free times and the booking keeps its coach and bay
 * (`keepAssignment`), both sent explicitly.
 */
import { useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import { resolveReservationsMaps } from '@/theme/maps/reservations';
import {
  cancelReservation,
  checkInReservation,
  getCancellationPreview,
  getReservation,
  markNoShow,
  overrideAcknowledgements,
  parseOverrideRefusal,
  parseStaffChange,
  updateReservation,
  type CancellationPreview,
  type ConflictRefusal,
  type NoShowCard,
  type RefundDirective,
  type Reservation,
  type StaffChange,
  type UpdateReservationInput,
} from '@/api/reservations';
import { useResource } from '@/hooks/useResource';
import { getService, listVariations, type V2Service } from '@/api/services';
import { useCalendarZone } from '@/location/useCalendarZone';
import { formatInstant } from '@/utils/instantFormat';
import { useMoneyPermissions, type MoneyPermission } from '@/auth/moneyPermissions';
import { isManagerPinRequired, managerPinFieldMessage } from '@/api/moneyRefusal';
import { useTenantCurrency } from '@/hooks/useTenantCurrency';
import { REFUND_IN_PROGRESS_MESSAGE, refundProcessingText } from '@/utils/refundInProgress';
import { formatMoney, minorUnitFactor } from '@/utils/v2Format';
import { ApiError } from '@/api/errors';
import { v2ErrorCode } from '@/api/v2Errors';
import { toReservationActionErrorMessage } from './reservationsError';
import { AvailabilitySlotPicker } from './AvailabilitySlotPicker';
import { ManualStartTime } from './ManualStartTime';
import { isCreditFunded } from './reservationPayment';
import {
  buildRefundOptions,
  DECLINE_PREVIEW_FAILED_NOTE,
  describeDecline,
  describePayment,
  describeTiming,
  FALLBACK_REFUND_OPTIONS,
  declineSendsMoney,
  isMoneyRefundChoice,
  refundInProgressOf,
  policyFallsShort,
  policyGivesFullRefund,
  PREVIEW_FAILED_NOTE,
  REFUND_LOCKED_NOTE,
  type CancelPreviews,
} from './cancelRefundCopy';
import { StaffChangeDialog } from './StaffChangeDialog';
import { OverrideConflictDialog } from './OverrideConflictDialog';
import { ManagerPinField } from './ManagerPinField';
import { isNoShowFeeQueued, NO_SHOW_FEE_WAIVE_NEEDS_MANAGER } from './noShowFee';
import type { AvailabilitySlot } from '@/api/availability';
import { refusedPicker } from './refusedPicker';
import { useKeepAssignment } from './keepAssignment';
import { BayCheckNotice, KeptAssignmentFields } from './KeptAssignmentFields';

/** Required-reason minimum (matches the handoff's "min ~4 chars" reason gate). */
const MIN_REASON = 4;

// --- Money conversion (DNKADM-0146 check-in override; mirrors UpcomingChargesScreen) --

/** Decimal places for a currency's minor unit (factor 1→0, 1000→3, else 2). */
function currencyDecimals(currency: string): number {
  const factor = minorUnitFactor(currency);
  return factor === 1 ? 0 : factor === 1000 ? 3 : 2;
}

/** A major-unit total → the amount-input display string (e.g. 54 USD → "54.00"). */
function majorToInput(amountMajor: number, currency: string | null): string {
  return currency ? amountMajor.toFixed(currencyDecimals(currency)) : String(amountMajor);
}

/** A major-unit input string → minor units (rounded), or null when not a finite number. */
function inputToMinor(value: string, currency: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * minorUnitFactor(currency));
}

// `isCreditFunded` moved to `./reservationPayment` (DNKADM-0350) so this component
// file only exports components (Fast Refresh) while still being reusable from the
// calendar's payment-state derivation — see that module's doc comment.

/**
 * DNKADM-0348 (HOTFIX): a booking already settled by card at booking time must never be offered a
 * second charge at check-in. dink-api stamps `paymentStatus` (`paid` on confirm, `pending` on
 * hold, `declined` on decline — the guest-paid AND member-paid routes share the writer); a
 * credit-funded or free booking leaves it null. Same fail-safe bias as `isCreditFunded`: ONLY a
 * null/absent status arms a charge — `pending`, `declined`, and any unrecognised future value all
 * suppress it. Under-proposing a charge is a conversation; over-proposing one takes the money twice.
 */
function isSettled(paymentStatus: unknown): boolean {
  return paymentStatus !== null && paymentStatus !== undefined;
}

const startDateOf = (iso: string | null): string | null => /^\d{4}-\d{2}-\d{2}/.exec(iso ?? '')?.[0] ?? null;

interface ActionDialogProps {
  open: boolean;
  reservation: Reservation;
  onClose: () => void;
  /** The location's IANA zone (DNKADM-0390 U2: the reschedule's manual start time); null → device. */
  timeZone?: string | null;
  /**
   * Success callback. The edit and reassign dialogs additionally pass the server's returned
   * reservation (DNKADM-0257) so callers can replace their copy in place instead of waiting
   * on the reconciling refetch; cancel passes only the message (its answer is a
   * `CancelResponse`, not a reservation), as do callers that only read the message.
   */
  onDone: (message: string, updated?: Reservation) => void;
  /**
   * DNKADM-0447: the caller's control is labelled "Decline" for a payment hold (`new`), so
   * it gets the decline dialog (no Refund choice, reason required, sends `full`) like a
   * pending request. Every other Cancel control leaves it unset.
   */
  asDecline?: boolean;
}

/**
 * Cancel-reservation modal — the ONE dialog every Cancel and Decline control opens (the
 * confirmation queue, the calendar drawer, the Now & Next board, the member panel). A
 * pending request (`unconfirmed`) is a decline and gets {@link DeclineRequestDialog}
 * (DNKADM-0446), and so does a payment hold (`new`) declined from Now & Next
 * (`asDecline`, DNKADM-0447); every other status gets {@link CancelBookingDialog}, unchanged.
 */
export function CancelReservationDialog(props: ActionDialogProps) {
  // DNKADM-0450: set when the decline's last-second re-read finds the booking confirmed (or
  // its hold settled) by someone else; the decline is dropped for the normal cancel dialog.
  // It carries the FRESH booking, so the cancel dialog's payment line is not the stale one.
  const [confirmedElsewhere, setConfirmedElsewhere] = useState<Reservation | null>(null);
  if (confirmedElsewhere !== null) {
    return (
      <CancelBookingDialog {...props} reservation={confirmedElsewhere} notice={JUST_CONFIRMED_NOTICE} />
    );
  }
  return props.reservation.status === 'unconfirmed' ||
    (props.asDecline === true && props.reservation.status === 'new') ? (
    <DeclineRequestDialog {...props} onConfirmedElsewhere={setConfirmedElsewhere} />
  ) : (
    <CancelBookingDialog {...props} />
  );
}

/** Shown on the cancel dialog a decline falls back to (DNKADM-0450). */
export const JUST_CONFIRMED_NOTICE = 'This booking was just confirmed. Choose how to cancel it.';

/** The line under the Refund choice when a NEW hold is cancelled on Full (DNKADM-0470). */
const NEW_HOLD_RELEASED_NOTE = 'The hold is released. Nothing is charged.';

const ALREADY_CANCELLED_NOTICE = 'This request was already cancelled.';

/**
 * DNKADM-0450: is the booking, as freshly read, still the pending request / unpaid hold the
 * decline was opened for? A pending request must still be `unconfirmed`; a payment hold
 * (`new`) must still be `new` and not settled (`paymentStatus` `paid`).
 */
function stillDeclinable(opened: Reservation, fresh: Reservation): boolean {
  if (opened.status === 'unconfirmed') return fresh.status === 'unconfirmed';
  return fresh.status === 'new' && fresh.paymentStatus?.trim().toLowerCase() !== 'paid';
}

/** A booking that stands (confirmed, showed, or a hold that was paid): cancel it, never decline. */
function isLiveBooking(fresh: Reservation): boolean {
  const status = fresh.status?.trim().toLowerCase();
  return (
    status === 'confirmed' ||
    status === 'showed' ||
    (status === 'new' && fresh.paymentStatus?.trim().toLowerCase() === 'paid')
  );
}

/**
 * Decline-request modal (DNKADM-0446). The studio refused the session and the member did
 * nothing wrong, so a decline always gives back in full: it sends `directive: 'full'`
 * (an explicit `policy` would override dink-api's own full-refund default for an
 * unconfirmed reject and could forfeit the credit or card money inside the cancellation
 * window). No Refund choice; instead one sentence, read from the `full` preview, says
 * what the member gets back. A REQUIRED reason gates submit, as on cancel.
 */
function DeclineRequestDialog({
  open,
  reservation,
  onClose,
  onDone,
  onConfirmedElsewhere,
}: ActionDialogProps & { onConfirmedElsewhere: (fresh: Reservation) => void }) {
  const ids = useId();
  const theme = useTheme();
  const t = resolveTokens(theme);
  const rm = resolveReservationsMaps(theme);
  const titleId = `${ids}-title`;
  const reasonId = `${ids}-reason`;
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const uuid = reservation.reservationUuid;

  // The one preview the decline sends: `full`. A failure keeps the decline working (the
  // server still gives back in full) and says the amounts could not be loaded.
  const preview = useResource<CancellationPreview | null>(
    () => (uuid ? getCancellationPreview(uuid, 'full') : Promise.resolve(null)),
    [uuid],
  );
  const declineText = preview.status === 'ready' && preview.data ? describeDecline(preview.data) : null;
  // DNKADM-0452 (Peyton): a refund of this booking's charge is already processing and declining
  // would send money back, so it waits until that refund settles. No money outcome: not blocked.
  const declineBlocked =
    preview.status === 'ready' &&
    preview.data != null &&
    preview.data.refundInProgress === true &&
    declineSendsMoney(preview.data);

  const reasonOk = reason.trim().length >= MIN_REASON;

  function close() {
    if (submitting) return;
    onClose();
  }

  async function handleConfirm() {
    setError(null);
    if (!reasonOk) {
      setError('Enter a reason for declining.');
      return;
    }
    if (!uuid) {
      setError('This request can’t be declined (missing id).');
      return;
    }
    setSubmitting(true);
    try {
      // DNKADM-0450: another staff member may have confirmed it (or the hold may have been
      // paid) since this dialog opened. A decline would then refund a booking that stands, so
      // re-read first; if it is no longer declinable, send nothing and hand over to Cancel.
      // A failed re-read blocks the decline (the safe side of a refund).
      const fresh = await getReservation(uuid).catch(() => null);
      if (fresh === null || !stillDeclinable(reservation, fresh)) {
        if (fresh !== null && isLiveBooking(fresh)) {
          onConfirmedElsewhere(fresh);
          return;
        }
        // Already cancelled, unreadable, or a status we do not know: send nothing, stay here.
        setError(
          fresh !== null && fresh.status?.trim().toLowerCase() === 'cancelled'
            ? ALREADY_CANCELLED_NOTICE
            : 'Couldn’t check the booking’s current status. Try again.',
        );
        setSubmitting(false);
        return;
      }
      await cancelReservation(uuid, {
        reason: reason.trim(),
        directive: 'full',
        expectedStatus: reservation.status?.trim().toLowerCase(),
      });
      onDone('Request declined.');
    } catch (err) {
      // DNKADM-0455: the server's lock found the booking in another status than the one this
      // decline was opened for, and wrote nothing. Same two outcomes as the re-read above.
      if (v2ErrorCode(err) === 'RESERVATION_STATUS_CHANGED') {
        if (err instanceof ApiError && err.details?.['currentStatus'] === 'cancelled') {
          onDone(ALREADY_CANCELLED_NOTICE);
          return;
        }
        const fresh = await getReservation(uuid).catch(() => null);
        if (fresh !== null && isLiveBooking(fresh)) {
          onConfirmedElsewhere(fresh);
          return;
        }
      }
      setError(toReservationActionErrorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}>
        <Avatar variant="rounded" sx={{ bgcolor: rm.dangerAvatarBg, color: t.error, width: 38, height: 38 }}>
          <Icon name="event_busy" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Decline request</Typography>
          <Typography variant="body2">{reservation.serviceName ?? 'Reservation'}</Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        {preview.status === 'loading' && (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, py: 1.5 }} role="status">
            <CircularProgress size={18} />
            <Typography sx={{ color: t.text2 }}>Calculating refund…</Typography>
          </Stack>
        )}

        {declineText !== null && (
          <Alert severity="info" icon={<Icon name="redeem" />} sx={{ mb: 2 }}>
            {declineText}
          </Alert>
        )}

        {declineBlocked && preview.status === 'ready' && preview.data && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {refundProcessingText(preview.data.refundInProgressAmount, preview.data.currency)}
            </Box>
            <Box component="span" sx={{ display: 'block' }}>
              {REFUND_IN_PROGRESS_MESSAGE}
            </Box>
          </Alert>
        )}

        {preview.status === 'error' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {DECLINE_PREVIEW_FAILED_NOTE}
          </Alert>
        )}

        <TextField
          id={reasonId}
          label="Reason for declining"
          required
          multiline
          minRows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
          placeholder="e.g. Coach unavailable, court closed for an event"
          {...(reasonOk ? { color: 'success' as const } : {})}
          {...(reason.length > 0 ? { focused: true } : {})}
          helperText={
            reason.length === 0
              ? 'Required. Logged to the audit trail so the team has context.'
              : reasonOk
                ? 'Looks good.'
                : 'A little more detail helps (at least a few words).'
          }
        />

        {error && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          Keep it
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<Icon name="cancel" />}
          onClick={() => void handleConfirm()}
          disabled={submitting || !reasonOk || declineBlocked}
        >
          {submitting ? 'Declining…' : 'Decline request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Cancel-booking modal (every status but a pending request). Fetches the three
 * cancellation previews on open (one per choice), says how the booking was paid and when
 * it starts, and lists each choice by what the member gets back (DNKADM-0445) — admin may
 * force `full` despite a member lock. A REQUIRED reason gates submit. On confirm →
 * `cancelReservation(uuid, { reason, directive })`, the directive value unchanged by the
 * labels.
 */
function CancelBookingDialog({
  open,
  reservation,
  onClose,
  onDone,
  notice,
}: ActionDialogProps & { notice?: string }) {
  const ids = useId();
  const theme = useTheme();
  const t = resolveTokens(theme);
  const rm = resolveReservationsMaps(theme);
  const titleId = `${ids}-title`;
  const refundId = `${ids}-refund`;
  const reasonId = `${ids}-reason`;
  // DNKADM-0470: a NEW payment hold (status `new`, not yet paid) has nothing to forfeit, so
  // cancelling it opens on Full: the hold is released and nothing is charged. A confirmed
  // booking (or a hold that was paid) keeps the Policy default. The note goes once the server
  // asks for the PIN: the hold was confirmed meanwhile, so "nothing is charged" is no longer true.
  const newHold =
    reservation.status === 'new' && reservation.paymentStatus?.trim().toLowerCase() !== 'paid';
  const [directive, setDirective] = useState<RefundDirective>(newHold ? 'full' : 'policy');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // DNKAPI-0670: Full and None on a CONFIRMED booking are money choices, read at the
  // booking's location. `false` hides them (a STAFF session at a location with no manager
  // PIN); `'pin'` asks for the manager PIN with them. Any other status needs no PIN. The
  // field also appears when the server answers MANAGER_PIN_REQUIRED, so a session the read
  // did not predict can still finish.
  const { canCancelFullOrNone } = useMoneyPermissions(reservation.locationId);
  const confirmedBooking = reservation.status === 'confirmed';
  const fullOrNone: MoneyPermission = confirmedBooking ? canCancelFullOrNone : true;
  const [managerPin, setManagerPin] = useState('');
  const [pinAsked, setPinAsked] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const pinShown = directive !== 'policy' && (fullOrNone === 'pin' || pinAsked);
  const pinMissing = pinShown && managerPin.trim().length === 0;

  const uuid = reservation.reservationUuid;

  // The cancellation previews (read-only refund math), one per choice so every option can
  // say what it gives back. Loaded once on open, in parallel; any failure degrades to the
  // generic options (cancel still works — the server applies the real refund per choice).
  const preview = useResource<CancelPreviews | null>(
    () =>
      uuid
        ? Promise.all([
            getCancellationPreview(uuid, 'policy'),
            getCancellationPreview(uuid, 'full'),
            getCancellationPreview(uuid, 'none'),
          ]).then(([policy, full, none]) => ({ policy, full, none }))
        : Promise.resolve(null),
    [uuid],
  );
  const data = preview.status === 'ready' ? preview.data : null;
  const options = buildRefundOptions(
    data,
    reservation.paymentStatus?.trim().toLowerCase() === 'unpaid',
  ).filter(
    (o) => o.value === 'policy' || fullOrNone !== false,
  );
  const paidText = data ? describePayment(reservation, data) : null;
  const timingText = data
    ? describeTiming(
        data.policy.leadHours,
        data.policy.policyWindowHours ?? data.full.policyWindowHours,
        policyGivesFullRefund(data),
      )
    : null;
  const refundLocked = data !== null && (data.policy.refundLocked || data.full.refundLocked);
  // DNKADM-0452: a refund of this booking’s charge is already processing, so no choice that
  // sends money back can be started (credits-only and nothing-back choices stay available).
  const processing = data ? refundInProgressOf(data) : null;
  const moneyBlocked = (value: RefundDirective): boolean =>
    data !== null && processing !== null && isMoneyRefundChoice(data, value);
  // Peyton’s ruling (DNKADM-0452): when the previews resolve with a refund processing and the
  // default choice sends money, preselect the first enabled option ONCE, so Cancel is never
  // left blocked. Keyed on the preview status; a later manual pick is never overridden.
  // DNKADM-0375 (Team Lead ruling): never preselect a choice that needs the manager PIN —
  // for a STAFF session that is Full or None — so the desk only lands on a PIN choice it
  // picked itself. With Policy blocked too, nothing is preselected and Cancel waits for a pick.
  const autoPicked = useRef(false);
  useEffect(() => {
    if (autoPicked.current || preview.status !== 'ready') return;
    autoPicked.current = true;
    if (!moneyBlocked(directive)) return;
    const needsPin = (value: RefundDirective) =>
      value !== 'policy' && fullOrNone === 'pin';
    const first = options.find((o) => !moneyBlocked(o.value) && !needsPin(o.value));
    if (first) setDirective(first.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.status]);

  const reasonOk = reason.trim().length >= MIN_REASON;

  function close() {
    if (submitting) return;
    onClose();
  }

  async function handleConfirm() {
    setError(null);
    if (!reasonOk) {
      setError('Enter a reason for this cancellation.');
      return;
    }
    if (!uuid) {
      setError('This reservation can’t be cancelled (missing id).');
      return;
    }
    if (pinMissing) {
      setPinError('Enter the manager PIN.');
      return;
    }
    setPinError(null);
    setSubmitting(true);
    try {
      await cancelReservation(uuid, {
        reason: reason.trim(),
        directive,
        ...(pinShown ? { managerPin: managerPin.trim() } : {}),
      });
      onDone('Reservation cancelled.');
    } catch (err) {
      const pinMessage = pinShown ? managerPinFieldMessage(err) : null;
      if (isManagerPinRequired(err)) setPinAsked(true);
      // DNKADM-0458: a failed request never leaves the manager PIN in the field.
      setManagerPin('');
      if (pinMessage !== null) setPinError(pinMessage);
      else setError(toReservationActionErrorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}>
        <Avatar variant="rounded" sx={{ bgcolor: rm.dangerAvatarBg, color: t.error, width: 38, height: 38 }}>
          <Icon name="event_busy" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Cancel reservation</Typography>
          <Typography variant="body2">{reservation.serviceName ?? 'Reservation'}</Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        {notice !== undefined && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {notice}
          </Alert>
        )}

        {/* How it was paid + when it starts, from the previews (DNKADM-0445). */}
        {preview.status === 'loading' && (
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, py: 1.5 }} role="status">
            <CircularProgress size={18} />
            <Typography sx={{ color: t.text2 }}>Calculating refund…</Typography>
          </Stack>
        )}

        {data && (paidText !== null || timingText !== null || refundLocked) && (
          <Box sx={{ mb: 2 }}>
            {(paidText !== null || timingText !== null) && (
              <Alert
                severity={policyFallsShort(data) ? 'warning' : 'info'}
                icon={<Icon name="redeem" />}
                sx={{ mb: refundLocked ? 1 : 0 }}
              >
                {paidText}
                {timingText !== null && (
                  <Box component="span" sx={{ display: 'block', fontSize: 12, mt: paidText ? 0.5 : 0 }}>
                    {timingText}
                  </Box>
                )}
              </Alert>
            )}

            {/* Refund-lock (a rescheduled booking, DNKADM-0447): Full still gives it all back. */}
            {refundLocked && (
              <Alert severity="info" sx={{ mt: 0.5 }}>
                {REFUND_LOCKED_NOTE}
              </Alert>
            )}
          </Box>
        )}

        {processing !== null && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {refundProcessingText(processing.amount, processing.currency)}
            </Box>
            <Box component="span" sx={{ display: 'block' }}>
              {REFUND_IN_PROGRESS_MESSAGE}
            </Box>
          </Alert>
        )}

        {preview.status === 'error' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {PREVIEW_FAILED_NOTE}
          </Alert>
        )}

        {/* Each option is labelled by its outcome; the values sent stay policy | full | none. */}
        <TextField
          id={refundId}
          select
          label="Refund"
          fullWidth
          size="small"
          value={directive}
          onChange={(e) => setDirective(e.target.value as RefundDirective)}
          disabled={submitting}
          sx={{ mb: 2 }}
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value} disabled={moneyBlocked(o.value)}>
              {o.label}
            </MenuItem>
          ))}
          {/* A choice picked before the previews collapsed it away stays selectable. */}
          {!options.some((o) => o.value === directive) && (
            <MenuItem value={directive} disabled={moneyBlocked(directive)}>
              {FALLBACK_REFUND_OPTIONS.find((o) => o.value === directive)?.label}
            </MenuItem>
          )}
        </TextField>

        {newHold && !pinAsked && directive === 'full' && (
          <Typography variant="body2" sx={{ color: t.text2, mb: 2, mt: -1 }}>
            {NEW_HOLD_RELEASED_NOTE}
          </Typography>
        )}

        {pinShown && (
          <ManagerPinField
            value={managerPin}
            onChange={(pin) => {
              setPinError(null);
              setManagerPin(pin);
            }}
            disabled={submitting}
            error={pinError}
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          id={reasonId}
          label="Reason for cancellation"
          required
          multiline
          minRows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
          placeholder="e.g. Member called to cancel, court flooded"
          {...(reasonOk ? { color: 'success' as const } : {})}
          {...(reason.length > 0 ? { focused: true } : {})}
          helperText={
            reason.length === 0
              ? 'Required. Logged to the audit trail so the team has context.'
              : reasonOk
                ? 'Looks good.'
                : 'A little more detail helps (at least a few words).'
          }
        />

        {error && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          Keep it
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<Icon name="cancel" />}
          onClick={() => void handleConfirm()}
          disabled={submitting || !reasonOk || moneyBlocked(directive) || pinMissing}
        >
          {submitting ? 'Cancelling…' : 'Cancel reservation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Reschedule-reservation modal — credit-neutral, via the shared availability slot
 * picker (the U2 picker) + a manual ISO start fallback, plus a REQUIRED reason. On
 * confirm → `updateReservation(uuid, { start, reason })`. Submit is blocked until a
 * start is chosen and the reason is valid.
 *
 * **A coach CHANGE is refused unless acknowledged (DNKAPI-0546, DNKADM-0393 U5).** When
 * the new time's free coach is not the one on the booking, the server answers `409
 * STAFF_WILL_CHANGE` and this opens the {@link StaffChangeDialog}: "Pick another time"
 * returns here with the picker untouched; "Move to <coach>" resends the IDENTICAL body
 * with `acknowledgeStaffChange: true`. When no coach would be assigned at all
 * (`proposedStaff: null`) only "Pick another time" is offered. `SLOT_TAKEN` — nobody
 * free at all — is a different code and keeps its existing inline message.
 *
 * **An admin override refusal is one dialog (DNKAPI-0549, DNKADM-0396).** A reschedule that
 * names an explicit coach or bay can come back `409 RESOURCE_CONFLICT` /
 * `STAFF_OUTSIDE_HOURS` / `STAFF_OVER_CAP` — several reasons in one body — and that opens
 * the {@link OverrideConflictDialog}: "Change it" returns here with the picker untouched,
 * "Book anyway" resends the IDENTICAL body with every acknowledge flag that refusal named.
 * A different code from `STAFF_WILL_CHANGE` on a different guard, read by its own seam and
 * never a fallback for it.
 *
 * **Reachable since DNKADM-0488:** dink-api enters `AdminOverrideGuard` only when the
 * reschedule body carries an explicit `staffId` or `resourceId`, and this dialog now sends the
 * booking's kept (or changed) coach and bay. The picker hides busy times for that coach and
 * bay, so the refusal is left for a race or a manual time.
 *
 * **Only free times, and the coach and bay are kept (DNKADM-0488).** The picker hides booked
 * times (`hideUnavailable`, as Book on behalf) and leaves this booking out of the occupancy it
 * measures (`excludeReservationUuid`, DNKAPI-0709). The booking's current coach and bay pin
 * the read and ride on the write — see `keepAssignment` for Change coach / Change bay and the
 * "Bay N is taken at this time" choice. A booking with neither sends `{ start, reason }` as
 * before.
 */
export function RescheduleDialog({ open, reservation, onClose, onDone, timeZone = null }: ActionDialogProps) {
  const ids = useId();
  const theme = useTheme();
  const t = resolveTokens(theme);
  const titleId = `${ids}-title`;
  const reasonId = `${ids}-reason`;
  const [slot, setSlot] = useState<AvailabilitySlot | null>(null);
  const [manualStart, setManualStart] = useState('');
  // The manual time's date when no picker owns one (DNKADM-0390 U2): the booking's own date.
  const [manualDate, setManualDate] = useState<string | null>(startDateOf(reservation.start));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // A `409 STAFF_WILL_CHANGE` (DNKADM-0393 U5): the server's answer plus the EXACT body
  // it refused, so "Move to <coach>" resends that body — not a re-derivation from state.
  const [staffChange, setStaffChange] = useState<{
    view: StaffChange;
    input: UpdateReservationInput;
  } | null>(null);
  // An admin-override refusal (DNKADM-0396) — the OTHER 409 family this save can get, with
  // the EXACT body it refused so "Book anyway" resends that body, flags and all.
  const [conflict, setConflict] = useState<{
    view: ConflictRefusal;
    input: UpdateReservationInput;
  } | null>(null);

  // The service's assignment modes (DNKADM-0488), read only when there is a coach or bay to keep.
  const hasAssignment = reservation.assignedStaffId !== null || reservation.assignedResourceId !== null;
  const service = useResource(
    () =>
      hasAssignment && reservation.serviceId
        ? getService(reservation.serviceId)
        : Promise.resolve<V2Service | null>(null),
    [hasAssignment, reservation.serviceId],
  );
  const keep = useKeepAssignment({
    reservation,
    staffMode: service.data?.staffAssignment ?? null,
    resourceMode: service.data?.resourceAssignment ?? null,
    // A new coach or bay re-reads the times; the picked one may not be free there.
    onPinsChange: () => {
      setSlot(null);
      setManualStart('');
    },
  });
  // The first read waits for the modes, so it is already pinned.
  const timesReady = !hasAssignment || service.status !== 'loading';

  const uuid = reservation.reservationUuid;
  const effectiveStart = slot?.start ?? (manualStart.trim() || null);
  const reasonOk = reason.trim().length >= MIN_REASON;
  const bayUnresolved = keep.bay.active && keep.bay.resolved === null;
  // The slot picker needs the booking's service/variation/location. When any is absent
  // (a degraded DTO) only the manual fallback is offered.
  const canPickSlots =
    reservation.serviceId !== null &&
    reservation.variationId !== null &&
    reservation.locationId !== null;

  function close() {
    if (submitting) return;
    onClose();
  }

  async function handleConfirm() {
    setError(null);
    if (!effectiveStart) {
      setError('Pick a new start time.');
      return;
    }
    if (!reasonOk) {
      setError('Enter a reason for this reschedule.');
      return;
    }
    if (!uuid) {
      setError('This reservation can’t be rescheduled (missing id).');
      return;
    }
    if (bayUnresolved) {
      setError('Pick a free bay for this time.');
      return;
    }
    // The kept (or changed) coach and bay, explicit so the server never re-picks them.
    await send(uuid, { start: effectiveStart, reason: reason.trim(), ...keep.writeIds });
  }

  /** The one write path — the first attempt and the acknowledged resend share it. */
  async function send(id: string, input: UpdateReservationInput) {
    setError(null);
    setSubmitting(true);
    try {
      await updateReservation(id, input);
      onDone('Reservation rescheduled.');
    } catch (err) {
      setSubmitting(false);
      const change = parseStaffChange(err);
      if (change) {
        setStaffChange({ view: change, input });
        return;
      }
      const refusal = parseOverrideRefusal(err);
      if (refusal) {
        setConflict({ view: refusal, input });
        return;
      }
      setError(toReservationActionErrorMessage(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      aria-labelledby={titleId}
      slotProps={{ paper: { sx: { maxWidth: 520 } } }}
    >
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}>
        <Avatar variant="rounded" sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}>
          <Icon name="edit_calendar" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Reschedule reservation</Typography>
          <Typography variant="body2">{reservation.serviceName ?? 'Reservation'}</Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>
      <DialogContent>
        {canPickSlots && reservation.serviceId && reservation.variationId && reservation.locationId ? (
          <Box sx={{ mb: 2 }}>
            <KeptAssignmentFields keep={keep} disabled={submitting} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Pick a new time</Typography>
            {/* The booking's member's availability (DNKADM-0390 U1): the admin route, keyed
                by the reservation's contact; a failed read falls back to the picker's manual time. */}
            {timesReady && (
              <AvailabilitySlotPicker
                contactId={reservation.contactId}
                serviceId={reservation.serviceId}
                variationId={reservation.variationId}
                locationId={reservation.locationId}
                {...keep.pickerProps}
                hideUnavailable
                timeZone={timeZone}
                selectedStart={slot?.start ?? null}
                onSelect={(s) => {
                  setSlot(s);
                  setManualStart('');
                  keep.onSlot(s);
                }}
                onManualStart={(iso) => {
                  setManualStart(iso ?? '');
                  setSlot(null);
                  keep.onSlot(null);
                }}
              />
            )}
            <BayCheckNotice keep={keep} disabled={submitting} />
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ mb: 1 }}>
              Available times can’t be loaded for this booking — pick a start time manually.
            </Alert>
            {/* No picker owns a date here (a degraded booking), so the date is editable too. */}
            <ManualStartTime
              date={manualDate}
              onDateChange={setManualDate}
              timeZone={timeZone}
              value={manualStart || null}
              onChange={(iso) => {
                setManualStart(iso ?? '');
                setSlot(null);
              }}
            />
          </Box>
        )}

        <TextField
          id={reasonId}
          label="Reason for reschedule"
          required
          multiline
          minRows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
          placeholder="e.g. Member requested a later slot"
          {...(reasonOk ? { color: 'success' as const } : {})}
          {...(reason.length > 0 ? { focused: true } : {})}
          helperText={
            reason.length === 0
              ? 'Required — logged to the audit trail so the team has context.'
              : reasonOk
                ? 'Looks good.'
                : 'A little more detail helps (at least a few words).'
          }
        />

        {error && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Icon name="check" />}
          onClick={() => void handleConfirm()}
          disabled={submitting || !reasonOk || effectiveStart === null || bayUnresolved}
        >
          {submitting ? 'Rescheduling…' : 'Confirm new time'}
        </Button>
      </DialogActions>

      {/* The coach-change warning (DNKADM-0393 U5) — a dialog over this one, never a
          toast. Mounted only while a refusal is pending, so "Pick another time" leaves
          the picker exactly as the operator left it. */}
      {staffChange && uuid && (
        <StaffChangeDialog
          open
          change={staffChange.view}
          action="reschedule"
          submitting={submitting}
          onPickAnotherTime={() => setStaffChange(null)}
          onMoveToProposed={() => {
            const { input } = staffChange;
            setStaffChange(null);
            void send(uuid, { ...input, acknowledgeStaffChange: true });
          }}
        />
      )}

      {/* The admin-override refusal (DNKADM-0396) — a dialog over this one, never a toast.
          Mounted only while a refusal is pending so its acknowledgement tick starts clean. */}
      {conflict && uuid && (
        <OverrideConflictDialog
          open
          refusal={conflict.view}
          submitting={submitting}
          onCancel={() => setConflict(null)}
          onChangeIt={() => {
            const field = refusedPicker(conflict.view);
            setConflict(null);
            // As Edit time: reveal the kept coach or bay the refusal points at (DNKADM-0488).
            if (field === 'staff' && keep.coach.active) keep.coach.startChange();
            if (field === 'resource' && keep.bay.active) keep.bay.startChange();
          }}
          onBookAnyway={() => {
            const { input, view } = conflict;
            setConflict(null);
            // Exactly the flags THIS refusal named — never one for a decision the admin
            // was not shown.
            void send(uuid, { ...input, ...overrideAcknowledgements(view) });
          }}
        />
      )}
    </Dialog>
  );
}

/**
 * Check-in (mark **Showed**) confirm-charge modal (DNKADM-0146) — opened by the shell
 * only for a **card-on-file** reservation (or the degrade case where the read omits
 * `paymentMode`). A non-card-on-file reservation skips this dialog entirely (the shell
 * calls `checkInReservation(uuid, {})` directly).
 *
 * The amount is **pre-filled with the ACTUAL card charge = `priceCharged + taxCharged`**
 * (price PLUS tax, major units) and converted to minor units for `amountMinorOverride`.
 * The **Charge / comp** toggle (default charge) drives the body: `charge:true` sends the
 * override; `charge:false` comps it (no override, no pending charge). Marking Showed on a
 * No-show reservation switches the status and re-prices the pending charge server-side —
 * the FE just calls the endpoint and the shell refetches. FORWARD CONTRACT (DNKAPI-0279).
 *
 * DNKAPI-0690 / DNKADM-0475: on a No-show booking whose fee is still queued, the comp
 * (`charge:false`) cancels that fee, so it is a waive under the DNKADM-0469 rules: STAFF
 * (`canWaiveNoShowFee` `'pin'`) enters the manager PIN with it; at a location with no PIN
 * set (`false`) the switch is locked on Charge, with the reason under it; and a
 * `MANAGER_PIN_REQUIRED` on any `charge:false` brings the PIN field up.
 */
export function CheckInDialog({ open, reservation, onClose, onDone }: ActionDialogProps) {
  const ids = useId();
  const theme = useTheme();
  const t = resolveTokens(theme);
  const titleId = `${ids}-title`;
  const amountId = `${ids}-amount`;
  const { currency } = useTenantCurrency();
  const creditFunded = isCreditFunded(
    reservation.creditsCharged,
    reservation.creditsChargedKnown,
  );
  // DNKADM-0348: already settled by card (or held/declined/unknown) — no second charge, ever.
  const settled = isSettled(reservation.paymentStatus);
  const chargeSuppressed = creditFunded || settled;

  // The actual card charge = price + tax (major units) — NOT priceCharged alone.
  const total = (reservation.priceCharged ?? 0) + (reservation.taxCharged ?? 0);
  const [amount, setAmount] = useState(() => majorToInput(total, currency));
  const [charge, setCharge] = useState(() => !chargeSuppressed);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // DNKAPI-0670: a changed check-in amount is an override under the same rule as the
  // no-show fee. `true` (an admin) keeps today's behavior — the amount is always sent.
  // Otherwise the amount is sent only when it differs from the booked price + tax (the
  // server's own default), with the manager PIN for `'pin'`; `false` locks the amount.
  const { canOverrideNoShowFee, canWaiveNoShowFee } = useMoneyPermissions(reservation.locationId);
  const [managerPin, setManagerPin] = useState('');
  const [pinAsked, setPinAsked] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  const uuid = reservation.reservationUuid;
  // DNKADM-0475: a comp here would cancel a queued no-show fee. With no manager PIN at the
  // location STAFF cannot waive it, so the switch stays on Charge.
  const feeQueued = isNoShowFeeQueued(reservation);
  const waiveBlocked = feeQueued && !chargeSuppressed && canWaiveNoShowFee === false;
  const effectiveCharge = !chargeSuppressed && (charge || waiveBlocked);
  // The override in minor units — only meaningful while charging with a known currency.
  const minor = effectiveCharge && currency ? inputToMinor(amount, currency) : null;
  const amountInvalid = effectiveCharge && currency !== null && (minor === null || minor < 0);
  const defaultMinor = currency ? inputToMinor(majorToInput(total, currency), currency) : null;
  const amountChanged = minor !== null && minor !== defaultMinor;
  const sendOverride = canOverrideNoShowFee === true || amountChanged;
  const overridePinShown =
    effectiveCharge && amountChanged && (canOverrideNoShowFee === 'pin' || pinAsked);
  // The waive asks up front only for a queued fee; any other `charge:false` asks once the
  // server answers MANAGER_PIN_REQUIRED.
  const waivePinShown = !effectiveCharge && ((feeQueued && canWaiveNoShowFee === 'pin') || pinAsked);
  const pinShown = overridePinShown || waivePinShown;
  const pinMissing = pinShown && managerPin.trim().length === 0;

  function close() {
    if (submitting) return;
    onClose();
  }

  async function handleConfirm() {
    setError(null);
    if (!uuid) {
      setError('This reservation can’t be checked in (missing id).');
      return;
    }
    if (amountInvalid) {
      setError('Enter an amount of 0 or more.');
      return;
    }
    if (pinMissing) {
      setPinError('Enter the manager PIN.');
      return;
    }
    setPinError(null);
    setSubmitting(true);
    try {
      // exactOptionalPropertyTypes: only assign `amountMinorOverride` when charging
      // with a resolved amount; a comp (or unknown currency) sends no override.
      const opts: { charge: boolean; amountMinorOverride?: number; managerPin?: string } = {
        charge: effectiveCharge,
      };
      if (effectiveCharge && minor !== null && sendOverride) {
        opts.amountMinorOverride = minor;
        if (overridePinShown) opts.managerPin = managerPin.trim();
      }
      if (waivePinShown) opts.managerPin = managerPin.trim();
      await checkInReservation(uuid, opts);
      onDone('Reservation checked in.');
    } catch (err) {
      const pinMessage = pinShown ? managerPinFieldMessage(err) : null;
      if (isManagerPinRequired(err)) setPinAsked(true);
      // DNKADM-0458: a failed request never leaves the manager PIN in the field.
      setManagerPin('');
      if (pinMessage !== null) setPinError(pinMessage);
      else setError(toReservationActionErrorMessage(err));
      setSubmitting(false);
    }
  }

  // Currency adornment only when a code is known (exactOptionalPropertyTypes).
  const amountSlotProps = currency
    ? { input: { startAdornment: <InputAdornment position="start">{currency}</InputAdornment> } }
    : undefined;

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}>
        <Avatar variant="rounded" sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}>
          <Icon name="check_circle" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Check in — mark Showed</Typography>
          <Typography variant="body2">{reservation.serviceName ?? 'Reservation'}</Typography>
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        {creditFunded ? (
          <Alert severity="info">This booking was paid with credits, so no card charge is proposed.</Alert>
        ) : settled ? (
          // DNKADM-0348: hidden, not disabled — a greyed amount still reads as "about to be charged".
          <Alert severity="info">This booking was already paid, so no card charge is proposed.</Alert>
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={charge || waiveBlocked}
                  onChange={(e) => setCharge(e.target.checked)}
                  disabled={submitting || waiveBlocked}
                />
              }
              label={charge || waiveBlocked ? 'Charge this card' : 'Don’t charge (comp)'}
              sx={{ mb: waiveBlocked ? 0 : 1 }}
            />
            {waiveBlocked && (
              <Typography variant="body2" sx={{ color: t.text2, mb: 1 }}>
                {NO_SHOW_FEE_WAIVE_NEEDS_MANAGER}
              </Typography>
            )}

            <TextField
              id={amountId}
              label="Amount"
              fullWidth
              size="small"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={submitting || !effectiveCharge || canOverrideNoShowFee === false}
              error={amountInvalid}
              {...(amountSlotProps ? { slotProps: amountSlotProps } : {})}
              helperText={
                !effectiveCharge
                  ? 'Comped — no charge is created.'
                  : amountInvalid
                    ? 'Enter an amount of 0 or more.'
                    : 'Charged ~30 min after the reservation ends.'
              }
            />
          </>
        )}

        {pinShown && (
          <ManagerPinField
            value={managerPin}
            onChange={(pin) => {
              setPinError(null);
              setManagerPin(pin);
            }}
            disabled={submitting}
            error={pinError}
            sx={{ mt: 2 }}
          />
        )}

        {error && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<Icon name="check_circle" />}
          onClick={() => void handleConfirm()}
          disabled={submitting || amountInvalid || pinMissing}
        >
          {submitting ? 'Checking in…' : 'Confirm check-in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** DNKADM-0482: when the no-show is read for the header and the charge box, "Wed, Oct 28, 2:00 PM". */
const NO_SHOW_WHEN = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
} as const satisfies Intl.DateTimeFormatOptions;

/** DNKADM-0482: "Visa ending 1234", degrading when the brand or the last four is unknown. */
function cardPhrase(card: NoShowCard): string {
  const { cardBrand: brand, cardLast4: last4 } = card;
  if (brand && last4) return `${brand} ending ${last4}`;
  if (last4) return `the card ending ${last4}`;
  if (brand) return brand;
  return 'the card';
}

const sameMoney = (a: number, b: number): boolean => Math.abs(a - b) < 0.005;

/**
 * No-show confirm-charge modal (DNKADM-0188). DNKAPI-0317 reads show the resolved fee,
 * whether a card can actually be charged, and minimal card display. Explicit credit
 * bookings and card bookings with no configured fee show truthful no-charge states;
 * pre-0317 reads (no card preview) lock the switch off and send no charge
 * (DNKADM-0482). A card + fee also permits an
 * optional major-unit override, converted to minor units for `amountMinorOverride`.
 *
 * DNKAPI-0678 / DNKADM-0469: waiving a fee that has a card to charge is a money override
 * too. `canWaiveNoShowFee` `'pin'` (a STAFF session) asks for the manager PIN with the
 * waive; `false` (no PIN set at the location) keeps the fee charged — the switch is locked
 * on, with the reason under it. A booking with no card has nothing to waive and never asks.
 *
 * DNKAPI-0677 / DNKADM-0468: a Pay later booking's fee is charged to the member's default
 * saved card. The card comes from `noShowCard` (a held card or that saved card; an older
 * server without it falls back to `cardOnFile`). A list read never names the saved card, so
 * for a Pay later booking the dialog reads `GET /{uuid}` on open and uses that read; while
 * it loads Mark no-show waits. If it fails the card is unconfirmed: the switch is locked
 * off with "Try again" beside the copy.
 *
 * DNKADM-0482: `charge:true` goes out only for a fee on a card shown in this dialog. A
 * failed saved-card read, a read with no card preview, and a read that found no card all
 * send `charge:false`; where the server treats that as a waive, a STAFF session gets
 * MANAGER_PIN_REQUIRED back and the PIN field comes up.
 */
export function NoShowDialog({ open, reservation, onClose, onDone }: ActionDialogProps) {
  const ids = useId();
  const theme = useTheme();
  const t = resolveTokens(theme);
  const rm = resolveReservationsMaps(theme);
  const titleId = `${ids}-title`;
  const amountId = `${ids}-amount`;
  const { currency } = useTenantCurrency();
  const calendarZone = useCalendarZone();
  const [amount, setAmount] = useState('');
  const [chargeChoice, setCharge] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // DNKAPI-0670: a custom fee amount is a money override. `false` hides the field; `'pin'`
  // (a STAFF session) asks for the manager PIN with it, as does a MANAGER_PIN_REQUIRED
  // answer. Charging the configured fee never needs the PIN. DNKAPI-0678: neither does
  // waiving it for an admin; STAFF waives with the PIN (`canWaiveNoShowFee`).
  const { canOverrideNoShowFee, canWaiveNoShowFee } = useMoneyPermissions(reservation.locationId);
  const [managerPin, setManagerPin] = useState('');
  const [pinAsked, setPinAsked] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  const uuid = reservation.reservationUuid;
  // DNKAPI-0677: a list read never names a Pay later booking's saved card (`noShowCard`
  // null, no card held, unpaid), so read the booking itself. An older server (no
  // `noShowCard` on the wire) is not asked.
  const readsSavedCard =
    uuid !== null &&
    reservation.noShowChargePreviewAvailable &&
    reservation.noShowCard === null &&
    !reservation.cardOnFile &&
    (reservation.paymentStatus ?? '').toLowerCase() === 'unpaid';
  const detail = useResource<Reservation | null>(
    () => (readsSavedCard && uuid ? getReservation(uuid) : Promise.resolve(null)),
    [uuid, readsSavedCard],
  );
  const savedCardLoading = readsSavedCard && detail.status === 'loading';
  const savedCardFailed = readsSavedCard && detail.status === 'error';
  const r = readsSavedCard && detail.data ? detail.data : reservation;

  const previewAvailable = r.noShowChargePreviewAvailable;
  const creditFunded = isCreditFunded(r.creditsCharged, r.creditsChargedKnown);
  // DNKADM-0435 (M-18): a booking that is already paid is never offered a no-show fee.
  // Also blocked when a payment may already have moved (DNKAPI-0563; mirrors calendar/derive.ts,
  // inlined here because derive.ts imports from this file).
  const payStatus = (r.paymentStatus ?? '').toLowerCase();
  const alreadyPaid = payStatus === 'paid';
  const paymentUnconfirmed =
    r.paymentNeedsReview === true ||
    r.paymentStranded === true ||
    (payStatus === 'pending' && (r.amountDue ?? 0) > 0);
  const feeBlocked = alreadyPaid || paymentUnconfirmed;
  // DNKAPI-0677: the card the fee is charged to — the held card or a Pay later booking's
  // saved card. A read without `noShowCard` (an older server) knows only the held card.
  const feeCard: NoShowCard | null =
    r.noShowCard !== undefined
      ? r.noShowCard
      : r.cardOnFile
        ? { source: 'hold', cardBrand: r.cardBrand, cardLast4: r.cardLast4 }
        : null;
  const canChargeFee =
    previewAvailable && !creditFunded && !feeBlocked && feeCard !== null && r.noShowFee !== null;
  // DNKADM-0469: a fee the server treats as waivable — a card to charge (held, or the
  // saved card since DNKAPI-0677) and a fee above $0 (DNKAPI-0678). A $0 fee has nothing to
  // waive and never asks for the PIN.
  const feeToWaive = canChargeFee && (r.noShowFee ?? 0) > 0;
  // No PIN set at the location — STAFF cannot waive that fee, so it stays charged (also
  // when the read lands after the switch was flipped).
  const waiveBlocked = feeToWaive && canWaiveNoShowFee === false;
  // DNKADM-0482: a card staff cannot see is never charged. When the saved-card read failed
  // or the booking read carries no card preview, the switch is off and locked and the
  // no-show goes out with no charge. "Try again" re-reads the card after a saved-card read
  // failure only; a missing preview has no retry.
  const cardUnconfirmed = !feeBlocked && (!previewAvailable || (!creditFunded && savedCardFailed));
  const charge = cardUnconfirmed ? false : chargeChoice || waiveBlocked;
  const overrideOffered = canChargeFee && canOverrideNoShowFee !== false;
  const hasOverride = overrideOffered && amount.trim().length > 0;
  const minor = canChargeFee && charge && currency ? inputToMinor(amount, currency) : null;
  const amountInvalid = canChargeFee && charge && hasOverride && (minor === null || minor <= 0);
  const overridePinShown =
    canChargeFee && charge && hasOverride && (canOverrideNoShowFee === 'pin' || pinAsked);
  // The `charge` flag this dialog sends. DNKADM-0482: `true` only for a fee on a card staff
  // saw in this dialog; every other case (paid, credit, $0 fee, no card, card unconfirmed)
  // sends `false`, so no charge goes out for a card nobody looked at.
  const sendCharge = !feeBlocked && canChargeFee && charge;
  // A waive asks for the PIN up front only when there is a fee to waive (a card + a fee
  // above $0); any other `charge:false` asks for it once the server answers
  // MANAGER_PIN_REQUIRED.
  const waivePinShown =
    !sendCharge && ((feeToWaive && canWaiveNoShowFee === 'pin') || pinAsked);
  const pinShown = overridePinShown || waivePinShown;
  const pinMissing = pinShown && managerPin.trim().length === 0;
  const feeLabel =
    r.noShowFee === null
      ? null
      : currency
        ? formatMoney(r.noShowFee, currency)
        : r.noShowFee.toFixed(2);
  // DNKADM-0482: the amount the click charges (a custom amount wins over the fee).
  const chargeAmountLabel =
    minor !== null && currency && minor > 0
      ? formatMoney(minor / minorUnitFactor(currency), currency)
      : feeLabel;
  const whenLabel = formatInstant(r.start ?? r.startTime, calendarZone, NO_SHOW_WHEN, '');
  const memberLabel = reservation.contactName ?? 'this member';
  const serviceLabel = reservation.serviceName ?? 'this booking';
  // DNKADM-0482: the reason line (fee vs booked price) is read from the booked variation,
  // because `noShowFee` on the wire is the already-resolved amount. Only fetched while a
  // charge is on offer; a failed or missing lookup falls back to the neutral line.
  const serviceId = r.serviceId;
  const variationId = r.variationId;
  const variations = useResource<Awaited<ReturnType<typeof listVariations>>>(
    () => (canChargeFee && serviceId ? listVariations(serviceId) : Promise.resolve([])),
    [canChargeFee, serviceId],
  );
  const variation =
    variationId !== null && variations.status === 'ready'
      ? (variations.data?.find((v) => v.id === variationId) ?? null)
      : null;
  const bookedPrice = r.priceCharged === null ? null : r.priceCharged + (r.taxCharged ?? 0);
  const resolvedFee = r.noShowFee;
  const reasonLine =
    hasOverride
      ? 'This is the custom amount entered below.'
      : variation !== null && resolvedFee !== null && variation.noShowFee !== null &&
          sameMoney(variation.noShowFee, resolvedFee)
        ? 'This is the service’s no-show fee.'
        : variation !== null && resolvedFee !== null && variation.noShowFee === null &&
            bookedPrice !== null && sameMoney(bookedPrice, resolvedFee)
          ? 'This service has no no-show fee set, so the booked price is charged.'
          : 'This is the amount a no-show charges for this booking.';

  function close() {
    if (submitting) return;
    onClose();
  }

  async function handleConfirm() {
    setError(null);
    if (!uuid) {
      setError('This reservation can’t be marked no-show (missing id).');
      return;
    }
    if (amountInvalid) {
      setError('Enter a custom amount greater than 0.');
      return;
    }
    if (pinMissing) {
      setPinError('Enter the manager PIN.');
      return;
    }
    setPinError(null);
    setSubmitting(true);
    try {
      const opts: { charge: boolean; amountMinorOverride?: number; managerPin?: string } = {
        charge: sendCharge,
      };
      if (canChargeFee && charge && hasOverride && minor !== null) {
        opts.amountMinorOverride = minor;
        if (overridePinShown) opts.managerPin = managerPin.trim();
      }
      if (waivePinShown) opts.managerPin = managerPin.trim();
      await markNoShow(uuid, opts);
      onDone('Reservation marked no-show.');
    } catch (err) {
      const pinMessage = pinShown ? managerPinFieldMessage(err) : null;
      if (isManagerPinRequired(err)) setPinAsked(true);
      // DNKADM-0458: a failed request never leaves the manager PIN in the field.
      setManagerPin('');
      if (pinMessage !== null) setPinError(pinMessage);
      else setError(toReservationActionErrorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}>
        <Avatar variant="rounded" sx={{ bgcolor: rm.dangerAvatarBg, color: t.error, width: 38, height: 38 }}>
          <Icon name="person_off" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Mark No-show</Typography>
          <Typography variant="body2">{reservation.contactName ?? 'Member'}</Typography>
          <Typography variant="body2">{reservation.serviceName ?? 'Reservation'}</Typography>
          {whenLabel !== '' && <Typography variant="body2">{whenLabel}</Typography>}
        </Box>
        <IconAction size="small" onClick={close} label="Close">
          <Icon name="close" sx={{ fontSize: 20 }} />
        </IconAction>
      </DialogTitle>

      <DialogContent>
        <Alert
          severity={(canChargeFee && charge) || savedCardFailed ? 'warning' : 'info'}
          icon={<Icon name="payments" />}
          sx={{ mb: 1.5 }}
          {...(savedCardFailed
            ? {
                action: (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={detail.refetch}
                    disabled={submitting}
                  >
                    Try again
                  </Button>
                ),
              }
            : {})}
        >
          {feeBlocked
            ? alreadyPaid
              ? 'Already paid — no no-show fee can be charged'
              : 'A payment on this booking is unconfirmed — no no-show fee can be charged'
            : cardUnconfirmed
              ? 'Can’t confirm the member’s card. Try again to charge a no-show fee, or mark the no-show with no charge.'
              : creditFunded
                ? 'No no-show fee (credit booking)'
                : savedCardLoading
                  ? 'Checking the member’s saved card…'
                  : feeCard === null
                    ? 'No card on file — no no-show fee can be charged'
                    : r.noShowFee === null
                      ? 'No no-show fee configured'
                      : !charge
                        ? 'No fee will be charged (waived).'
                        : (
                            <>
                              <Box component="span" sx={{ display: 'block' }}>
                                {`Marking ${memberLabel} as a no-show for ${serviceLabel}${whenLabel ? ` on ${whenLabel}` : ''} charges ${chargeAmountLabel ?? r.noShowFee} to ${cardPhrase(feeCard)} (${feeCard.source === 'saved_card' ? 'their saved card' : 'the card held for this booking'}).`}
                              </Box>
                              <Box component="span" sx={{ display: 'block' }}>
                                {reasonLine}
                              </Box>
                            </>
                          )}
        </Alert>

        {!feeBlocked && (cardUnconfirmed || canChargeFee) && (
          <FormControlLabel
            control={
              <Switch
                checked={charge}
                onChange={(e) => setCharge(e.target.checked)}
                disabled={submitting || waiveBlocked || cardUnconfirmed}
              />
            }
            label={charge || cardUnconfirmed ? 'Charge the no-show fee' : 'Waive the fee'}
          />
        )}

        {waiveBlocked && (
          <Typography variant="body2" sx={{ color: t.text2 }}>
            This location has no manager PIN set, so staff can’t waive this fee. Ask an owner or
            admin.
          </Typography>
        )}

        {overrideOffered && (
          <TextField
            id={amountId}
            label={`Custom amount (optional; default ${feeLabel ?? r.noShowFee})`}
            fullWidth
            size="small"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={submitting || !charge}
            error={amountInvalid}
            {...(currency
              ? {
                  slotProps: {
                    input: { startAdornment: <InputAdornment position="start">{currency}</InputAdornment> },
                  },
                }
              : {})}
            helperText={
              !charge
                ? 'Waived — no charge is created.'
                : !currency && hasOverride
                  ? 'Currency is not available yet.'
                  : amountInvalid
                  ? 'Enter a custom amount greater than 0.'
                  : 'Leave blank to use the configured fee.'
            }
            sx={{ mt: 1 }}
          />
        )}

        {pinShown && (
          <ManagerPinField
            value={managerPin}
            onChange={(pin) => {
              setPinError(null);
              setManagerPin(pin);
            }}
            disabled={submitting}
            error={pinError}
            sx={{ mt: 2 }}
          />
        )}

        {error && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={<Icon name="person_off" />}
          onClick={() => void handleConfirm()}
          disabled={submitting || savedCardLoading || amountInvalid || pinMissing}
        >
          {submitting
            ? 'Marking…'
            : sendCharge
              ? `Mark no-show and charge ${chargeAmountLabel ?? r.noShowFee}`
              : 'Mark no-show, no charge'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
